import type { Opportunity } from './types'
import { sortOpps } from './format'

/**
 * Copy the actionable (HOT + WARM) opportunities to the clipboard as CSV, ready to paste
 * into Excel. Ported from the prototype's exportCSV(): same columns, same BOM-prefixed
 * plain-text ClipboardItem (which stops browsers writing HTML to the clipboard), same
 * writeText fallback. The prototype's final fallback — a full-screen textarea to hand-copy
 * from — is gone; if the browser blocks the clipboard entirely we tell the user via the
 * caller's toast instead of taking over the page.
 *
 * Resolves with the number of rows copied; rejects if the clipboard is unavailable.
 */
export async function exportCsv(all: Opportunity[]): Promise<number> {
  const rows = sortOpps(all.filter((n) => n.band === 'HOT' || n.band === 'WARM'))

  const headers = [
    'Band',
    'Score',
    'Title',
    'Buyer',
    'Value',
    'Deadline',
    'Type',
    'Capabilities',
    'Why it fits',
    'Next action',
    'Action detail',
    'Risks',
    'Decision',
  ]
  const escape = (v: string | number | null | undefined) => '"' + String(v ?? '').replace(/"/g, '""') + '"'

  const csv = [
    headers.map(escape).join(','),
    ...rows.map((n) =>
      [
        n.band,
        n.fitScore ?? '',
        n.title,
        n.buyer,
        n.value || 'TBC',
        n.deadline || 'TBC',
        n.noticeType || 'Tender',
        (n.capabilities || []).join('; '),
        n.why,
        n.action,
        n.actionDetail,
        n.risks || '',
        '',
      ]
        .map(escape)
        .join(','),
    ),
  ].join('\n')

  try {
    const item = new ClipboardItem({ 'text/plain': new Blob(['\uFEFF' + csv], { type: 'text/plain' }) })
    await navigator.clipboard.write([item])
  } catch {
    // ClipboardItem unsupported or refused — plain writeText loses the BOM but still pastes.
    await navigator.clipboard.writeText(csv)
  }
  return rows.length
}
